"use client";

import { useState } from "react";
import { useBuilder } from "@/context/BuilderContext";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useBuilderOptions } from "@/hooks/useBuilderOptions";

const COLOR_GROUPS = [
  {
    label: "Solid Colors",
    colors: [
      { value: "Natural Black", price: 0, hex: "#1a1a1a", label: "Natural Black" },
      { value: "Dark Brown", price: 0, hex: "#3b2314", label: "Dark Brown" },
      { value: "Medium Brown", price: 0, hex: "#6b4423", label: "Medium Brown" },
      { value: "Light Brown", price: 0, hex: "#a0744f", label: "Light Brown" },
      { value: "Blonde", price: 200, hex: "#d4a856", label: "Blonde" },
      { value: "Red / Auburn", price: 100, hex: "#8b3a2a", label: "Red / Auburn" },
      { value: "Burgundy", price: 100, hex: "#6d1a36", label: "Burgundy" },
    ],
  },
  {
    label: "With Highlights",
    colors: [
      { value: "Brown with Highlights", price: 300, hex: "#6b4423", label: "Brown + Highlights", extra: "#d4a856" },
      { value: "Blonde with Highlights", price: 400, hex: "#d4a856", label: "Blonde + Highlights", extra: "#f5e6b8" },
      { value: "Black with Highlights", price: 300, hex: "#1a1a1a", label: "Black + Highlights", extra: "#8b6914" },
      { value: "Red with Highlights", price: 350, hex: "#8b3a2a", label: "Red + Highlights", extra: "#d4a856" },
    ],
  },
  {
    label: "Ombre",
    colors: [
      { value: "Ombre — Dark to Light", price: 350, hex: "#1a1a1a", label: "Dark to Light", extra: "#d4a856", isOmbre: true },
      { value: "Ombre — Brown to Blonde", price: 350, hex: "#6b4423", label: "Brown to Blonde", extra: "#d4a856", isOmbre: true },
      { value: "Ombre — Black to Brown", price: 300, hex: "#1a1a1a", label: "Black to Brown", extra: "#6b4423", isOmbre: true },
    ],
  },
  {
    label: "Custom",
    colors: [
      { value: "Custom Color Match", price: 500, hex: "#888", label: "Custom Match", isCustom: true },
    ],
  },
];

export function StepColor() {
  const { selections, setOption } = useBuilder();
  const selected = selections.color;
  const { options } = useBuilderOptions();
  const isCustomSelected = selected?.startsWith("Custom:") || false;
  const [customText, setCustomText] = useState(isCustomSelected ? (selected || "").replace("Custom: ", "") : "");

  // Override prices from DB where available
  const dbPrices = new Map((options.color || []).map((o) => [o.value, o.price]));
  const groups = COLOR_GROUPS.map((group) => ({
    ...group,
    colors: group.colors.map((c) => ({
      ...c,
      price: dbPrices.has(c.value) ? dbPrices.get(c.value)! : c.price,
    })),
  }));

  return (
    <div className="space-y-6 pt-2">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-brand-muted mb-3">
            {group.label}
          </p>
          <div className="flex flex-wrap gap-3">
            {group.colors.map((color) => {
              const isCustom = "isCustom" in color;
              const isSelected = isCustom ? (selected?.startsWith("Custom") || false) : selected === color.value;
              const hasExtra = "extra" in color && color.extra;
              const isOmbre = "isOmbre" in color;

              const swatchStyle = isCustom
                ? { background: "conic-gradient(#f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)" }
                : isOmbre && hasExtra
                  ? { background: `linear-gradient(180deg, ${color.hex} 0%, ${color.extra} 100%)` }
                  : hasExtra
                    ? { background: `linear-gradient(135deg, ${color.hex} 60%, ${color.extra} 100%)` }
                    : { backgroundColor: color.hex };

              return (
                <button
                  key={color.value}
                  onClick={() => {
                    if (isCustom) {
                      setOption("color", customText ? `Custom: ${customText}` : "Custom Color Match", color.price);
                    } else {
                      setCustomText("");
                      setOption("color", color.value, color.price);
                    }
                  }}
                  className={cn(
                    "group relative flex flex-col items-center gap-1.5 rounded-lg p-2.5 transition-all duration-300 min-w-[70px] min-h-[44px]",
                    isSelected
                      ? "bg-brand-gold/10 ring-2 ring-brand-gold"
                      : "hover:bg-gray-50"
                  )}
                >
                  <div
                    className={cn(
                      "h-12 w-12 sm:h-12 sm:w-12 rounded-full border-2 shadow-sm transition-transform duration-300",
                      isSelected ? "border-brand-gold scale-110" : "border-gray-200 group-hover:scale-105"
                    )}
                    style={swatchStyle}
                  >
                    {isSelected && (
                      <div className="flex h-full w-full items-center justify-center">
                        <Check className="h-4 w-4 text-white drop-shadow-md" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                  <span className={cn(
                    "text-[11px] leading-tight text-center max-w-[65px]",
                    isSelected ? "text-brand-dark font-medium" : "text-brand-muted"
                  )}>
                    {color.label}
                  </span>
                  {color.price > 0 && (
                    <span className="text-[10px] text-brand-gold font-medium">
                      +{formatPrice(color.price)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {(selected?.startsWith("Custom") || false) && (
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-[11px] font-medium uppercase tracking-[0.15em] text-brand-muted mb-2">
            Describe your desired color
          </label>
          <input
            type="text"
            value={customText}
            onChange={(e) => {
              setCustomText(e.target.value);
              if (e.target.value.trim()) {
                setOption("color", `Custom: ${e.target.value.trim()}`, 500);
              }
            }}
            placeholder="e.g., Ash blonde with honey highlights, dark roots fading to caramel..."
            className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm text-brand-dark placeholder:text-gray-400 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/50 outline-none transition-colors"
            maxLength={200}
          />
          <p className="text-[10px] text-brand-muted mt-1.5">
            Be as specific as possible — include shade, tones, and any reference photos in the notes step.
          </p>
        </div>
      )}
    </div>
  );
}
